<script setup>
/**
 * DAVMAN AI INTEGRATION
 * Este archivo es parte de la integración personalizada de IA para Davman Persa.
 * NO es parte del código original de Chatwoot.
 *
 * Panel de Sugerencias de IA para agentes
 */

import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from 'dashboard/composables/store';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  inboxId: {
    type: Number,
    default: undefined,
  },
});

const emit = defineEmits(['use-suggestion']);

const store = useStore();
const pollingInterval = ref(null);
const copySuccess = ref(false);

// Computed properties
const suggestion = computed(() => {
  return store.getters['davmanAi/getSuggestion'](props.conversationId);
});

const isLoading = computed(() => {
  return store.getters['davmanAi/isLoading'](props.conversationId);
});

const hasSuggestion = computed(() => {
  return store.getters['davmanAi/hasSuggestion'](props.conversationId);
});

const isServiceAvailable = computed(() => {
  return store.getters['davmanAi/isServiceAvailable'];
});

const error = computed(() => {
  return store.getters['davmanAi/getError'](props.conversationId);
});

// Intent display configuration
const intentConfig = {
  COMPRA: { emoji: '🛍️', label: 'Interés de Compra', color: '#4CAF50' },
  PRECIO: { emoji: '💰', label: 'Consulta de Precio', color: '#FF9800' },
  STOCK: { emoji: '📦', label: 'Disponibilidad', color: '#2196F3' },
  CITA: { emoji: '📅', label: 'Agendar Cita', color: '#9C27B0' },
  QUEJA: { emoji: '⚠️', label: 'Queja/Reclamo', color: '#F44336' },
  DEVOLUCION: { emoji: '↩️', label: 'Devolución', color: '#FF5722' },
  FAQ: { emoji: '❓', label: 'Pregunta Frecuente', color: '#607D8B' },
  SEGUIMIENTO: { emoji: '🔍', label: 'Seguimiento', color: '#795548' },
  SALUDO: { emoji: '👋', label: 'Saludo', color: '#87785B' },
  COMPLEJO: { emoji: '🔴', label: 'Atención Especial', color: '#E91E63' },
  OTRO: { emoji: '💬', label: 'Otro', color: '#9E9E9E' },
};

const intentDisplay = computed(() => {
  if (!suggestion.value?.intent) return null;
  return intentConfig[suggestion.value.intent] || intentConfig.OTRO;
});

// Methods
const fetchSuggestion = () => {
  store.dispatch('davmanAi/fetchSuggestion', props.conversationId);
};

const handleUseSuggestion = async () => {
  if (!suggestion.value?.content) return;

  // Copiar al portapapeles
  await copyToClipboard(suggestion.value.content);

  // Marcar como usada
  await store.dispatch('davmanAi/markAsUsed', props.conversationId);

  // Emitir evento para que el componente padre pueda actuar
  emit('use-suggestion', suggestion.value.content);
};

const handleCopySuggestion = async () => {
  if (!suggestion.value?.content) return;

  await copyToClipboard(suggestion.value.content);
  copySuccess.value = true;

  setTimeout(() => {
    copySuccess.value = false;
  }, 2000);
};

const handleDismiss = async () => {
  await store.dispatch('davmanAi/dismiss', props.conversationId);
};

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (err) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  }
};

const startPolling = () => {
  // Fetch inicial
  fetchSuggestion();

  // Polling cada 3 segundos
  pollingInterval.value = setInterval(() => {
    if (!hasSuggestion.value) {
      fetchSuggestion();
    }
  }, 3000);
};

const stopPolling = () => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value);
    pollingInterval.value = null;
  }
};

// Watch conversation changes
watch(
  () => props.conversationId,
  (newId, oldId) => {
    if (newId !== oldId) {
      store.dispatch('davmanAi/clearSuggestion', oldId);
      fetchSuggestion();
    }
  }
);

// Lifecycle
onMounted(() => {
  startPolling();
});

onUnmounted(() => {
  stopPolling();
});
</script>

<template>
  <div class="davman-ai-panel">
    <!-- Estado: Servicio no disponible -->
    <div v-if="!isServiceAvailable" class="davman-ai-status davman-ai-status--offline">
      <span class="status-icon">⚠️</span>
      <span class="status-text">Servicio IA no disponible</span>
    </div>

    <!-- Estado: Cargando -->
    <div v-else-if="isLoading && !hasSuggestion" class="davman-ai-loading">
      <div class="loading-spinner"></div>
      <span class="loading-text">Generando sugerencia...</span>
    </div>

    <!-- Estado: Error -->
    <div v-else-if="error" class="davman-ai-status davman-ai-status--error">
      <span class="status-icon">❌</span>
      <span class="status-text">{{ error }}</span>
    </div>

    <!-- Estado: Sin sugerencia -->
    <div v-else-if="!hasSuggestion" class="davman-ai-empty">
      <span class="empty-icon">💡</span>
      <span class="empty-text">Esperando mensaje del cliente...</span>
    </div>

    <!-- Estado: Sugerencia disponible -->
    <div v-else class="davman-ai-suggestion">
      <!-- Intent Badge -->
      <div v-if="intentDisplay" class="intent-badge" :style="{ backgroundColor: intentDisplay.color }">
        <span class="intent-emoji">{{ intentDisplay.emoji }}</span>
        <span class="intent-label">{{ intentDisplay.label }}</span>
      </div>

      <!-- Contenido de la sugerencia -->
      <div class="suggestion-content">
        {{ suggestion.content }}
      </div>

      <!-- Botones de acción -->
      <div class="suggestion-actions">
        <button
          class="action-btn action-btn--primary"
          @click="handleUseSuggestion"
        >
          <span class="btn-icon">📝</span>
          Usar respuesta
        </button>

        <button
          class="action-btn action-btn--secondary"
          :class="{ 'action-btn--success': copySuccess }"
          @click="handleCopySuggestion"
        >
          <span class="btn-icon">{{ copySuccess ? '✓' : '📋' }}</span>
          {{ copySuccess ? 'Copiado' : 'Copiar' }}
        </button>

        <button
          class="action-btn action-btn--ghost"
          @click="handleDismiss"
        >
          <span class="btn-icon">✕</span>
        </button>
      </div>

      <!-- Metadata -->
      <div class="suggestion-meta">
        <span class="meta-item">{{ suggestion.model }}</span>
        <span class="meta-separator">•</span>
        <span class="meta-item">Confianza: {{ suggestion.confidence || 'N/A' }}</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// Variables de marca Davman Persa
$davman-bronze: #87785B;
$davman-bronze-light: #A89A7D;
$davman-bronze-dark: #6B6049;

.davman-ai-panel {
  padding: 12px;
  background-color: rgba($davman-bronze, 0.05);
  border-radius: 8px;
}

// Estados
.davman-ai-status,
.davman-ai-loading,
.davman-ai-empty {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-radius: 6px;
  font-size: 13px;

  .status-icon,
  .loading-spinner,
  .empty-icon {
    flex-shrink: 0;
  }
}

.davman-ai-status--offline {
  background-color: #FFF3E0;
  color: #E65100;
}

.davman-ai-status--error {
  background-color: #FFEBEE;
  color: #C62828;
}

.davman-ai-empty {
  background-color: #F5F5F5;
  color: #757575;
}

.davman-ai-loading {
  background-color: rgba($davman-bronze, 0.1);
  color: $davman-bronze-dark;

  .loading-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba($davman-bronze, 0.3);
    border-top-color: $davman-bronze;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

// Sugerencia
.davman-ai-suggestion {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.intent-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  color: white;
  width: fit-content;

  .intent-emoji {
    font-size: 14px;
  }
}

.suggestion-content {
  padding: 12px;
  background-color: white;
  border: 1px solid rgba($davman-bronze, 0.2);
  border-left: 3px solid $davman-bronze;
  border-radius: 6px;
  font-size: 14px;
  line-height: 1.5;
  color: #333;
  white-space: pre-wrap;
}

.suggestion-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  .btn-icon {
    font-size: 14px;
  }

  &:hover {
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
}

.action-btn--primary {
  background-color: $davman-bronze;
  color: white;

  &:hover {
    background-color: $davman-bronze-dark;
  }
}

.action-btn--secondary {
  background-color: rgba($davman-bronze, 0.1);
  color: $davman-bronze-dark;

  &:hover {
    background-color: rgba($davman-bronze, 0.2);
  }

  &.action-btn--success {
    background-color: #E8F5E9;
    color: #2E7D32;
  }
}

.action-btn--ghost {
  background-color: transparent;
  color: #999;
  padding: 8px 10px;

  &:hover {
    background-color: #f5f5f5;
    color: #666;
  }
}

.suggestion-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #999;
  padding-top: 4px;
  border-top: 1px solid #eee;
}

.meta-separator {
  color: #ddd;
}
</style>
